import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Scale, Briefcase, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingModal from '../components/BookingModal';
import ShootingStars from '../components/ShootingStars';
import { practiceAreas } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Home = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentJusticeImage, setCurrentJusticeImage] = useState(0);

  const justiceImages = [
    'https://guipotlqykzxndlnervl.supabase.co/storage/v1/object/public/blackrose/download%20(2).png',
    'https://guipotlqykzxndlnervl.supabase.co/storage/v1/object/public/blackrose/image-removebg-preview.png'
  ];

  // Slides are generated from the practice areas so the two always match
  const carouselImages = [
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1000&h=800&fit=crop',
    'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1000&h=800&fit=crop'
  ];

  const carouselItems = practiceAreas.map((area, i) => ({
    title: area.title,
    subtitle: area.tag,
    description: area.description,
    panel: area.panel,
    image: carouselImages[i % carouselImages.length]
  }));

  const [firmOverviewRef, firmOverviewVisible] = useScrollAnimation({ threshold: 0.2, once: true });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.2, once: true });
  const [practiceRef, practiceVisible] = useScrollAnimation({ threshold: 0.1, once: true });
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.3, once: true });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselItems.length]);

  useEffect(() => {
    const justiceTimer = setInterval(() => {
      setCurrentJusticeImage((prev) => (prev + 1) % justiceImages.length);
    }, 8000);
    return () => clearInterval(justiceTimer);
  }, [justiceImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  // Swipe handlers
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <>
      <Helmet>
        <title>Valentis Law Group | International Dispute Resolution & Enforcement</title>
        <meta name="description" content="Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets." />
        <meta name="keywords" content="International dispute resolution, enforcement work, cross border law firm, India, China, Asian markets, Middle Eastern markets, recovery claims, commercial disputes" />
        <link rel="canonical" href="https://valentislawgroup.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Valentis Law Group | International Dispute Resolution & Enforcement" />
        <meta property="og:description" content="Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets." />
        <meta property="og:url" content="https://valentislawgroup.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://guipotlqykzxndlnervl.supabase.co/storage/v1/object/public/blackrose/SHARE.png" />

        {/* Twitter */}
        <meta name="twitter:title" content="Valentis Law Group | International Dispute Resolution & Enforcement" />
        <meta name="twitter:description" content="Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets." />
        <meta name="twitter:image" content="https://guipotlqykzxndlnervl.supabase.co/storage/v1/object/public/blackrose/SHARE.png" />
      </Helmet>

      <div className="bg-cream text-ink">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Lady Justice Background Images Slideshow - Behind everything */}
          {/* <div className="absolute left-0 right-0 top-0 bottom-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
          {justiceImages.map((image, index) => (
            <div
              key={index}
              className={`absolute left-0 right-0 top-0 bottom-0 flex items-center md:items-end pointer-events-none pb-0 ${
                index === 0 ? 'justify-end md:pr-8 lg:pr-12 xl:pr-16' : 'justify-start md:pl-8 lg:pl-12 xl:pl-16'
              }`}
              style={{
                opacity: currentJusticeImage === index ? 1 : 0,
                transition: 'opacity 3s ease-in-out',
                transitionDelay: currentJusticeImage === index ? '1s' : '0s'
              }}
            >
              <img 
                src={image}
                alt={`Lady Justice ${index + 1}`}
                className="h-[65vh] md:h-[75vh] lg:h-[80vh] xl:h-[85vh] w-auto object-contain object-center md:object-bottom"
                style={{ 
                  filter: 'grayscale(100%) brightness(0.8)',
                  mixBlendMode: 'lighten',
                  maxWidth: '100%',
                  willChange: 'opacity'
                }}
              />
            </div>
          ))}
        </div> */}

          {/* Shooting Stars */}
          <div className="absolute inset-0" style={{ zIndex: 5 }}>
            <ShootingStars />
          </div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0" style={{ zIndex: 5 }}>
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-ink/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }}></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ink/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12" style={{ transform: `translateY(${scrollY * 0.2}px)`, zIndex: 10 }}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center pt-24 lg:pt-0">
            <div className="lg:col-span-8 text-center">
            {/* Main Logo/Heading */}
            <div className="mb-8 lg:mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h1 className="font-serif text-7xl md:text-8xl lg:text-9xl tracking-wider mb-4 text-ink" style={{ textShadow: 'none' }}>
                VALENTIS
              </h1>
              <p className="text-xl md:text-2xl tracking-[0.3em] text-ink/80 uppercase" style={{ textShadow: 'none' }}>
                Law Group
              </p>
              <p className="mt-5 text-sm md:text-base tracking-[0.25em] text-ink/60 uppercase">
                Part of Malhotra Law Partners
              </p>
            </div>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-ink/70 mb-8 lg:mb-6 max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s', textShadow: 'none' }}>
              International Dispute Resolution & Enforcement
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Button
                onClick={() => setIsBookingOpen(true)}
                size="lg"
                className="bg-ink text-cream hover:bg-ink/90 px-10 py-6 text-base font-bold tracking-wider transition-all duration-300 hover:scale-105 border-2 border-ink min-w-[280px]"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-ink text-ink hover:bg-ink hover:text-cream px-10 py-6 text-base font-bold tracking-wider transition-all duration-300 hover:scale-105 min-w-[280px]"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                <Link to="/practice-areas">Explore Practice Areas</Link>
              </Button>
            </div>
            </div>

            {/* Founder portrait */}
            <div className="lg:col-span-4 flex justify-center lg:justify-end animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              <figure className="w-56 md:w-64 lg:w-full lg:max-w-xs">
                <div className="p-2 bg-sand-100 border border-ink/20 shadow-xl">
                  <img
                    src="/images/daggar-malhotra.jpg"
                    alt="Daggar Malhotra, Founding & Managing Partner"
                    className="w-full aspect-[4/5] object-cover object-center"
                  />
                </div>
                <figcaption className="mt-4 text-center">
                  <p className="font-serif text-xl text-ink">Daggar Malhotra</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-ink/60 leading-relaxed">
                    Founding & Managing Partner
                  </p>
                </figcaption>
              </figure>
            </div>
            </div>
          </div>
        </section>

        {/* Practice Areas Carousel */}
        <section className="py-16 lg:py-24 bg-sand-50 border-t border-ink/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div
              className="relative overflow-hidden bg-cream border border-ink/15 shadow-xl group"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Slides */}
              <div className="relative h-[660px] md:h-[460px]">
                {carouselItems.map((item, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 grid grid-cols-1 md:grid-cols-2 grid-rows-[14rem_1fr] md:grid-rows-1 transition-all duration-700 ${index === currentSlide
                      ? 'opacity-100 translate-x-0'
                      : index < currentSlide
                        ? 'opacity-0 -translate-x-full'
                        : 'opacity-0 translate-x-full'
                      }`}
                  >
                    {/* Text panel */}
                    <div className="order-2 md:order-1 flex flex-col justify-center px-8 md:px-12 lg:px-16 pt-8 pb-20 md:py-12">
                      <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-4">{item.subtitle}</p>
                      <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-wide leading-tight mb-5 text-ink">
                        {item.title}
                      </h2>
                      <p className="text-base md:text-lg text-ink/75 leading-relaxed mb-4">
                        {item.description}
                      </p>
                      {item.panel && (
                        <p className="inline-block self-start bg-ink text-cream px-3 py-1 text-xs tracking-wider mb-5">
                          {item.panel}
                        </p>
                      )}
                      <Link
                        to="/practice-areas"
                        className="inline-flex items-center text-sm tracking-wider text-ink hover:text-ink/70 transition-colors duration-300"
                      >
                        Learn More
                        <ArrowRight className="ml-2" size={16} />
                      </Link>
                    </div>

                    {/* Image panel, toned to the site palette */}
                    <div className="order-1 md:order-2 relative overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'grayscale(0.55) sepia(0.25) contrast(1.05)' }}
                        loading={index === 0 ? 'eager' : 'lazy'}
                      />
                      <div className="absolute inset-0 bg-ink/15 mix-blend-multiply"></div>
                      <div className="hidden md:block absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-cream to-transparent"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center bg-cream/90 hover:bg-ink text-ink hover:text-cream border border-ink/20 rounded-full p-3 transition-all duration-300 hover:scale-110 z-10 opacity-0 group-hover:opacity-100"
                aria-label="Previous slide"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={nextSlide}
                className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center bg-cream/90 hover:bg-ink text-ink hover:text-cream border border-ink/20 rounded-full p-3 transition-all duration-300 hover:scale-110 z-10 opacity-0 group-hover:opacity-100"
                aria-label="Next slide"
              >
                <ChevronRight size={22} />
              </button>

              {/* Slide counter */}
              <div className="absolute bottom-6 left-8 md:left-12 lg:left-16 flex items-center gap-4 z-10">
                <span className="text-xs tracking-[0.25em] text-ink/60">
                  {String(currentSlide + 1).padStart(2, '0')} / {String(carouselItems.length).padStart(2, '0')}
                </span>
                <div className="relative h-px w-24 bg-ink/20">
                  <div
                    className="absolute inset-y-0 left-0 h-px bg-ink transition-all duration-500"
                    style={{ width: `${((currentSlide + 1) / carouselItems.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Firm Overview */}
        <section ref={firmOverviewRef} className="py-24 lg:py-32 border-t border-ink/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className={`space-y-8 transition-all duration-1000 ${firmOverviewVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide leading-tight">
                  About The Firm
                </h2>
                <div className="space-y-6 text-ink/70 text-lg leading-relaxed">
                  <p>
                    Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets.
                  </p>
                  <p>
                    The firm primarily supports businesses based in foreign jurisdictions, Non Resident Indians, expatriates, and international corporate clients who require litigation strategy and execution support in foreign courts and regulatory forums.
                  </p>
                  <p>
                    The firm works extensively on matters involving recovery claims, commercial disputes, contractual enforcement, financial exposure, and regulatory litigation, especially where counterparties or assets are located in India.
                  </p>
                </div>
                <Button
                  asChild
                  className="bg-ink text-cream hover:bg-ink/90 font-medium tracking-wider transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <Link to="/about">
                    Learn More About Us
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>

              <div ref={statsRef} className={`bg-ink/5 backdrop-blur-sm border border-ink/10 p-12 transition-all duration-1000 delay-200 ${statsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                <div className="text-center space-y-6">
                  <Award size={64} className="text-ink mx-auto" />
                  <h3 className="font-serif text-4xl md:text-5xl">Multiple Landmark Cases</h3>
                  <p className="text-lg text-ink/70">Successfully resolved complex legal matters across Indian courts and tribunals</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Practice Areas Preview */}
        <section ref={practiceRef} className="py-24 lg:py-32 bg-sand-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className={`text-center mb-16 transition-all duration-1000 ${practiceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <p className="text-sm uppercase tracking-widest text-ink/60 mb-4">Our Expertise</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide">Practice Areas &amp; Sectors</h2>
            </div>

            {[...new Set(practiceAreas.map((a) => a.category))].map((category) => (
              <div key={category} className="mb-16">
                <h3 className="font-serif text-2xl md:text-3xl tracking-wide mb-8 pb-4 border-b border-ink/15">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {practiceAreas.filter((a) => a.category === category).map((area, index) => (
                <div
                  key={area.id}
                  className={`group bg-cream border border-ink/10 p-8 hover:border-ink/30 transition-all duration-700 hover:scale-105 hover:shadow-2xl cursor-pointer ${practiceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                    }`}
                  style={{
                    transitionDelay: practiceVisible ? `${index * 0.15}s` : '0s'
                  }}
                >
                  <p className="text-xs uppercase tracking-[0.25em] text-ink/50 mb-3">{area.tag}</p>
                  <h3 className="font-serif text-2xl mb-4 group-hover:text-ink/90 transition-colors duration-300">{area.title}</h3>
                  <p className="text-ink/60 leading-relaxed mb-4">{area.description}</p>
                  {area.panel && (
                    <p className="inline-block bg-ink text-cream px-3 py-1 text-xs tracking-wider mb-6">{area.panel}</p>
                  )}
                  <Link
                    to="/practice-areas"
                    className="inline-flex items-center text-sm tracking-wider text-ink/80 hover:text-ink transition-colors duration-300"
                  >
                    Learn More
                    <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" size={16} />
                  </Link>
                </div>
              ))}
                </div>
              </div>
            ))}

            <div className={`text-center mt-12 transition-all duration-1000 delay-700 ${practiceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-ink text-ink hover:bg-ink hover:text-cream px-8 py-6 font-medium tracking-wider transition-all duration-300 hover:scale-105"
              >
                <Link to="/practice-areas">View All Practice Areas</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section ref={ctaRef} className="py-24 lg:py-32 bg-sand-50 relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-ink/5 to-transparent"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-ink/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
          </div>

          <div className={`max-w-4xl mx-auto px-6 lg:px-12 text-center relative z-10 transition-all duration-1000 ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide mb-8">
              Facing a High-Stakes Dispute?
            </h2>
            <p className="text-xl text-ink/70 mb-12 leading-relaxed">
              Our world-class arbitration team is ready to defend your interests in billion-dollar disputes. Don't leave your future to chance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsBookingOpen(true)}
                size="lg"
                className="bg-ink text-cream hover:bg-ink/90 px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-110 hover:shadow-2xl"
              >
                Schedule Consultation
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-ink text-ink hover:bg-ink hover:text-cream px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-105"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </section>

        <BookingModal open={isBookingOpen} onOpenChange={setIsBookingOpen} />
      </div>
    </>
  );
};

export default Home;